import { AppData, TechlogEntry as LegacyTechlogEntry, AircraftStats as LegacyAircraftStats } from '../types';
import {
  getTechlogEntries,
  createTechlogEntry,
  updateTechlogEntry,
  getOrCreatePilot,
  getOrCreateAircraft,
  getAircraftWithStats,
  clearAllData as clearSupabaseData,
} from './supabaseQueries';
import { calculateAndStorePilotStatistics } from './pilotStatisticsQueries';
import type { TechlogEntryWithRelations } from '../types/database';

// =====================================================
// CONVERSION FUNCTIONS
// =====================================================

const convertSupabaseTechlogToLegacy = (
  entry: TechlogEntryWithRelations
): LegacyTechlogEntry => {
  return {
    id: entry.id,
    techlogNumber: entry.techlog_number,
    date: entry.date,
    aircraftRegistration: entry.aircraft?.registration || '',
    pilotName: entry.pilot?.name || '',
    coPilotName: entry.copilot?.name,
    flightType: (entry.flight_type || 'S-Schedule') as any,
    sectors: (entry.sectors || []).map((s) => ({
      id: s.id,
      departure: s.departure_airport,
      arrival: s.arrival_airport,
      takeoffTime: s.takeoff_time,
      landingTime: s.landing_time,
      flightTime: Number(s.flight_time_hours),
      itt: Number(s.itt),
      fuelUplift: s.fuel_uplift ? Number(s.fuel_uplift) : undefined,
      oilUplift: s.oil_uplift ? Number(s.oil_uplift) : undefined,
    })),
    createdAt: new Date(entry.created_at),
    // Trend data from techlog entry
    oat: entry.oat ? Number(entry.oat) : undefined,
    alt: entry.alt ? Number(entry.alt) : undefined,
    ias: entry.ias ? Number(entry.ias) : undefined,
    tas: entry.tas ? Number(entry.tas) : undefined,
    torque: entry.torque ? Number(entry.torque) : undefined,
    propRpm: entry.prop_rpm ? Number(entry.prop_rpm) : undefined,
    itt: entry.itt ? Number(entry.itt) : undefined,
    ng: entry.ng ? Number(entry.ng) : undefined,
    oilPress: entry.oil_press ? Number(entry.oil_press) : undefined,
    oilTemp: entry.oil_temp ? Number(entry.oil_temp) : undefined,
    fuelFlow: entry.fuel_flow ? Number(entry.fuel_flow) : undefined,
  };
};

// =====================================================
// PUBLIC API (maintains compatibility with existing code)
// =====================================================

export const loadFromSupabase = async (): Promise<AppData> => {
  try {
    console.log('📥 Loading data from Supabase...');
    const [techlogEntries, aircraftWithStats] = await Promise.all([
      getTechlogEntries(),
      getAircraftWithStats(),
    ]);

    console.log(`✓ Loaded ${techlogEntries.length} techlog entries from database`);
    console.log(`✓ Loaded ${aircraftWithStats.length} aircraft from database`);
    
    // Debug: Check sectors
    if (techlogEntries.length > 0) {
      console.log('Sample entry sectors:', techlogEntries[0].sectors?.length || 0);
    }

    const legacyTechlogs = techlogEntries.map(convertSupabaseTechlogToLegacy);
    
    console.log(`✓ Converted ${legacyTechlogs.length} entries`);
    if (legacyTechlogs.length > 0) {
      console.log('Sample converted entry sectors:', legacyTechlogs[0].sectors?.length || 0);
    }
    
    const aircraftStats: LegacyAircraftStats[] = aircraftWithStats.map((a: any) => ({
      registration: a.registration,
      totalCycles: a.statistics?.total_cycles || 0,
      totalFlightHours: Number(a.statistics?.total_flight_hours || 0),
      lastUpdated: a.statistics?.updated_at
        ? new Date(a.statistics.updated_at)
        : new Date(),
    }));

    console.log('📊 Aircraft Stats from Database:');
    aircraftStats.forEach(stat => {
      console.log(`  ${stat.registration}: ${stat.totalCycles} cycles, ${stat.totalFlightHours}h`);
    });

    return {
      techlogEntries: legacyTechlogs,
      aircraftStats,
    };
  } catch (error) {
    console.error('Error loading from Supabase:', error);
    return {
      techlogEntries: [],
      aircraftStats: [],
    };
  }
};

export const addTechlogEntryToSupabase = async (
  entry: LegacyTechlogEntry
): Promise<void> => {
  try {
    // Get or create pilot
    const pilot = await getOrCreatePilot(entry.pilotName);
    
    // Get or create copilot if exists
    let copilot = null;
    if (entry.coPilotName) {
      copilot = await getOrCreatePilot(entry.coPilotName);
    }

    // Get or create aircraft
    const aircraft = await getOrCreateAircraft(entry.aircraftRegistration);

    // Prepare sectors
    const sectors = entry.sectors.map((s, index) => ({
      departure_airport: s.departure,
      arrival_airport: s.arrival,
      takeoff_time: s.takeoffTime,
      landing_time: s.landingTime,
      flight_time_hours: s.flightTime,
      sector_order: index + 1,
      itt: s.itt,
      fuel_uplift: s.fuelUplift || null,
      oil_uplift: s.oilUplift || null,
    }));

    // Create techlog entry with trend data
    await createTechlogEntry(
      {
        techlog_number: entry.techlogNumber,
        date: entry.date,
        aircraft_id: aircraft.id,
        pilot_id: pilot.id,
        copilot_id: copilot?.id || null,
        flight_type: entry.flightType,
        oat: entry.oat || null,
        alt: entry.alt || null,
        ias: entry.ias || null,
        tas: entry.tas || null,
        torque: entry.torque || null,
        prop_rpm: entry.propRpm || null,
        itt: entry.itt || null,
        ng: entry.ng || null,
        oil_press: entry.oilPress || null,
        oil_temp: entry.oilTemp || null,
        fuel_flow: entry.fuelFlow || null,
      },
      sectors
    );

    // Update pilot statistics for pilot
    await calculateAndStorePilotStatistics(entry.pilotName);
    
    // Update pilot statistics for co-pilot if exists
    if (entry.coPilotName) {
      await calculateAndStorePilotStatistics(entry.coPilotName);
    }
  } catch (error) {
    console.error('Error adding techlog entry to Supabase:', error);
    throw error;
  }
};

export const updateTechlogEntryInSupabase = async (
  entry: LegacyTechlogEntry
): Promise<void> => {
  try {
    // Get or create pilot
    const pilot = await getOrCreatePilot(entry.pilotName);
    
    // Get or create copilot if exists
    let copilot = null;
    if (entry.coPilotName) {
      copilot = await getOrCreatePilot(entry.coPilotName);
    }

    // Get or create aircraft
    const aircraft = await getOrCreateAircraft(entry.aircraftRegistration);

    // Prepare sectors
    const sectors = entry.sectors.map((s, index) => ({
      departure_airport: s.departure,
      arrival_airport: s.arrival,
      takeoff_time: s.takeoffTime,
      landing_time: s.landingTime,
      flight_time_hours: s.flightTime,
      sector_order: index + 1,
      itt: s.itt,
      fuel_uplift: s.fuelUplift || null,
      oil_uplift: s.oilUplift || null,
    }));

    // Update techlog entry with trend data
    await updateTechlogEntry(
      entry.id,
      {
        techlog_number: entry.techlogNumber,
        date: entry.date,
        aircraft_id: aircraft.id,
        pilot_id: pilot.id,
        copilot_id: copilot?.id || null,
        flight_type: entry.flightType,
        oat: entry.oat || null,
        alt: entry.alt || null,
        ias: entry.ias || null,
        tas: entry.tas || null,
        torque: entry.torque || null,
        prop_rpm: entry.propRpm || null,
        itt: entry.itt || null,
        ng: entry.ng || null,
        oil_press: entry.oilPress || null,
        oil_temp: entry.oilTemp || null,
        fuel_flow: entry.fuelFlow || null,
      },
      sectors
    );

    // Update pilot statistics
    await calculateAndStorePilotStatistics(entry.pilotName);
    if (entry.coPilotName) {
      await calculateAndStorePilotStatistics(entry.coPilotName);
    }
  } catch (error) {
    console.error('Error updating techlog entry in Supabase:', error);
    throw error;
  }
};

export const clearAllSupabaseData = async (): Promise<void> => {
  try {
    await clearSupabaseData();
  } catch (error) {
    console.error('Error clearing Supabase data:', error);
    throw error;
  }
};

// =====================================================
// BACKWARD COMPATIBILITY WITH LOCALSTORAGE
// =====================================================

// Keep the old localStorage functions for migration purposes
export const migrateLocalStorageToSupabase = async (): Promise<void> => {
  try {
    const STORAGE_KEY = 'crewStatusTrackerData';
    const serializedData = localStorage.getItem(STORAGE_KEY);
    
    if (!serializedData) {
      console.log('No localStorage data to migrate');
      return;
    }

    const data = JSON.parse(serializedData);
    const techlogEntries = data.techlogEntries?.map((entry: any) => ({
      ...entry,
      createdAt: new Date(entry.createdAt),
    })) || [];

    console.log(`Migrating ${techlogEntries.length} entries to Supabase...`);

    // Migrate each entry
    for (const entry of techlogEntries) {
      await addTechlogEntryToSupabase(entry);
    }

    console.log('Migration completed successfully');
  } catch (error) {
    console.error('Error migrating data:', error);
    throw error;
  }
};

